import { type FC } from "react";
import { Check, Mail, Phone, CircleCheckBig, UserCheck } from "lucide-react";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";
import { LAWYER_PLANS } from "@/shared/constants/lawyerPlans";

import { SecurityNote } from "@/shared/components/ui/security-note";

import type { LawyerPlanKey } from "@/shared/constants/lawyerPlans";

interface ClientInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface ReviewStepProps {
  clientInfo: ClientInfo;
  selectedPlan: LawyerPlanKey | null;
  firstClientOfferAvailable: boolean;
  onBack: () => void;
  onEditClient: () => void;
  onSend: () => void;
  isSending: boolean;
}

export const ReviewStep: FC<ReviewStepProps> = ({
  clientInfo,
  selectedPlan,
  firstClientOfferAvailable,
  onBack,
  onEditClient,
  onSend,
  isSending,
}) => {
  const plan = selectedPlan ? LAWYER_PLANS[selectedPlan] : null;
  const initials =
    `${clientInfo.firstName.charAt(0)}${clientInfo.lastName.charAt(0)}`.toUpperCase();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col gap-6 overflow-y-auto bg-subtle p-6">
        <div className="flex flex-col gap-1">
          <h4 className="text-xl font-semibold">Review and send</h4>
          <p className="text-sm text-secondary">
            {plan
              ? "Confirm client details and plan recommendation before anything is sent"
              : "Confirm client details and plan options before anything is sent"}
          </p>
        </div>

        <div className="flex flex-col items-start gap-4 sm:flex-row">
          {plan ? (
            <div className="flex min-w-0 flex-1 flex-col gap-6 rounded-lg border border-primary bg-white p-4">
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <span className="text-2xs">Recommended plan</span>
                  <h4 className="text-xl font-semibold">{plan.name}</h4>
                  {plan.sources && <span className="text-2xs text-tertiary">{plan.sources}</span>}
                  <p className="text-sm text-secondary">
                    {plan.reviewDescription.replace(/\{name\}/g, clientInfo.firstName)}
                  </p>
                </div>
                <Tag color="green" className="w-fit">
                  {plan.savingsNote}
                </Tag>
              </div>

              <div className="-mx-4 border-t border-primary" />
              <div>
                <p className="mb-2 text-sm text-secondary">Includes</p>
                <div className="flex flex-col gap-2">
                  {plan.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                      <span className="text-sm text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 flex-col gap-6 rounded-lg border border-primary bg-white p-4">
              <div className="flex flex-col gap-1">
                <span className="text-2xs">No recommendation</span>
                <h4 className="text-xl font-semibold">{clientInfo.firstName} chooses a plan</h4>
                <p className="text-sm text-secondary">
                  Without a recommendation, {clientInfo.firstName} picks from all three plans on
                  their own. You&apos;ll be notified once they select one.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-sm text-secondary">Plans {clientInfo.firstName} will see</p>
                <ul className="flex flex-col gap-2 pl-1">
                  <li className="flex items-start gap-2 text-sm text-secondary">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>
                      <span className="font-semibold text-primary">Estate Setup,</span>{" "}
                      source-linked record, guided intake, organized next steps
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-secondary">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span className="flex flex-col">
                      <span className="font-semibold text-primary">Asset Search</span>
                      <span className="text-2xs text-tertiary">
                        {LAWYER_PLANS.asset_discovery.sources}
                      </span>
                      <span>Adds IRS-authorized asset and debt discovery</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-secondary">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>
                      <span className="font-semibold text-primary">Estate Settlement,</span> adds
                      transfer steps, EIN support, attorney-visible estate account monitoring
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          <div className="flex w-full flex-col gap-4 sm:w-[275px] sm:shrink-0">
            <div className="flex flex-col gap-3 rounded-lg border border-primary bg-white p-4">
              <div className="flex items-center justify-between">
                <span className="text-2xs text-secondary">Client information</span>
                <button
                  type="button"
                  onClick={onEditClient}
                  className="text-sm font-medium text-brand"
                >
                  Edit
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand text-2xs font-semibold text-white">
                  {initials}
                </div>
                <span className="text-sm font-semibold">
                  {clientInfo.firstName} {clientInfo.lastName}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-secondary" />
                  <span className="text-2xs text-secondary">{clientInfo.email}</span>
                </div>
                {clientInfo.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-secondary" />
                    <span className="text-2xs text-secondary">{clientInfo.phone}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col items-center gap-3 rounded-lg border border-primary bg-white p-4">
              <div className="flex items-start gap-2">
                <CircleCheckBig className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                <span className="text-2xs text-secondary">
                  {!firstClientOfferAvailable
                    ? "Paid plans are billed as an estate expense. No charge to your firm."
                    : selectedPlan === "estate_administration"
                      ? "Your first-client credit covers the first $3,000. The estate pays the remaining $3,000. No charge to your firm."
                      : selectedPlan === null
                        ? `Your first-client credit applies to whichever plan ${clientInfo.firstName} selects. Estate Setup and Asset Search are fully covered; Estate Settlement is $3,000 after the credit is applied.`
                        : "Free for your first client. No charge to the estate or your firm."}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <UserCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                <span className="text-2xs text-secondary">
                  {selectedPlan === null
                    ? `No charge to your firm. Final price is set when ${clientInfo.firstName} selects a plan.`
                    : selectedPlan === "estate_administration"
                      ? `${clientInfo.firstName} accepts the plan and price before anything is charged.`
                      : `${clientInfo.firstName} accepts the plan before anything is charged. They can upgrade anytime.`}
                </span>
              </div>
            </div>
          </div>
        </div>

        <SecurityNote text="Bank-level encryption and financial-grade security. Your client's records stay protected." />
      </div>

      <div className="flex justify-between border-t border-primary px-6 py-4">
        <Button variant="outline" onClick={onBack} disabled={isSending}>
          Back
        </Button>
        <Button variant="brand" onClick={onSend} disabled={isSending}>
          {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send invite"}
        </Button>
      </div>
    </div>
  );
};
